import { selectOccurrencePanelFilter } from "./occurrencePanelSelectors";
import {
  changeOccurrenceFilterModeThunk,
  changeOccurrenceFilterValueThunk,
} from "./validationPanelThunks";
import React from "react";
import "./DrofusOccurrencePanel.css";
import {
  Alert,
  Box,
  Button,
  Card,
  Divider,
  Group as MGroup,
  Menu,
  ScrollArea,
  SegmentedControl,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { FiChevronDown, FiCheck } from "react-icons/fi";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { ArticleValidationExclusionsTable } from "./ArticleValidationExclusionsTable";
import { selectNoActiveArticleRows } from "./articleValidationExclusionSelectors";
import { formatShortDate } from "../globalUtils";
import {
  selectSpecifiedArticlesValidation,
  selectSpecifiedArticlesDiffItems,
  selectSpecifiedArticlesMatchRatePct,
  selectSpecifiedArticlesValidatedAt,
} from "../DesignTab/ModuleBars/DrofusOccurrenceValidationBarSelectors";

import { DrofusRoomOccurrencesLiveTable } from "./DrofusRoomOccurrencesLiveTable";
import { RevalidateDrofusOccurrenceValidationButton } from "./RevalidateDrofusOccurrenceValidationButton";

type FilterOption = {
  value: string | null;
  label: string;
};

const makePlaceholder = (w = 14) => <span style={{ width: w, display: "inline-block" }} />;

const DrofusOccurrenceValidationFilterMenu: React.FC<{
  label: string;
  value: string | null;
  emptyLabel: string;
  options: FilterOption[];
  disabled?: boolean;
  tooltip: string;
  onChange: (value: string | null) => void;
}> = ({ label, value, emptyLabel, options, disabled, tooltip, onChange }) => {
  const activeLabel = value ?? emptyLabel;

  return (
    <Stack gap={3} style={{ minWidth: 0 }}>
      <Text size="xs" c="dimmed" fw={600}>
        {label}
      </Text>

      <Menu withinPortal position="bottom-start" shadow="md" width={260}>
        <Menu.Target>
          <Tooltip label={tooltip} withArrow position="bottom" openDelay={250}>
            <Button
              size="xs"
              variant={value ? "filled" : "light"}
              color="blue"
              radius="md"
              disabled={disabled}
              rightSection={<FiChevronDown size={14} />}
              styles={{
                root: { maxWidth: 190 },
                label: { minWidth: 0 },
              }}
            >
              <Text size="xs" fw={600} truncate>
                {activeLabel}
              </Text>
            </Button>
          </Tooltip>
        </Menu.Target>

        <Menu.Dropdown>
          <ScrollArea.Autosize mah={260} type="auto">
            {options.map((option) => {
              const isSelected = option.value === value;
              return (
                <Menu.Item
                  key={option.value ?? "__all__"}
                  onClick={() => onChange(option.value)}
                  leftSection={isSelected ? <FiCheck size={14} /> : makePlaceholder()}
                >
                  <Text size="sm" truncate>
                    {option.label}
                  </Text>
                </Menu.Item>
              );
            })}
          </ScrollArea.Autosize>
        </Menu.Dropdown>
      </Menu>
    </Stack>
  );
};

export const DrofusSpecifiedArticlesDiffAllResponsibilities: React.FC = () => {
  const dispatch = useAppDispatch();

  const specified = useAppSelector(selectSpecifiedArticlesValidation);
  const {
    filterMode,
    activeFilterOptions,
    activeFilterValue,
    activeFilterLabel,
    activeFilterEmptyLabel,
    activeFilterTooltip,
    activeFilterDisabled,
  } = useAppSelector(selectOccurrencePanelFilter);
  const noActiveRows = useAppSelector(selectNoActiveArticleRows);
  const diffs = useAppSelector(selectSpecifiedArticlesDiffItems);
  const matchRatePct = useAppSelector(selectSpecifiedArticlesMatchRatePct);
  const validatedAt = useAppSelector(selectSpecifiedArticlesValidatedAt);

  return (
    <Card
      withBorder
      radius="md"
      w="100%"
      h="100%"
      className="occurrence-panel"
      p="sm"
      style={{ display: "flex", flexDirection: "column", minHeight: 0, overflowY: "auto" }}
    >
      <div className="occurrence-summary">
        <div>
          <span className="occurrence-eyebrow">DROFUS · FOREKOMSTER</span>
          <div className="occurrence-score">
            <strong>{noActiveRows || matchRatePct == null ? "—" : `${Number(matchRatePct).toFixed(1)} %`}</strong>
            <span>{noActiveRows ? "Ingen artikler å validere" : "Treffsikkerhet"}</span>
          </div>
        </div>
        <span className="occurrence-date">
          {validatedAt ? `Sist validert ${formatShortDate(validatedAt)}` : "Ikke validert"}
        </span>
      </div>
      <Stack gap="xs" align="flex-start" style={{ flex: "0 0 auto" }}>
        <Box w="100%">
          <Stack gap="xs">
            <MGroup justify="space-between" align="flex-start" gap="sm">
              <Stack gap={6} style={{ minWidth: 0, flex: "1 1 auto" }}>
                <MGroup gap="sm" align="flex-end" wrap="wrap" style={{ minWidth: 0 }}>
                  <Stack gap={3}>
                    <Text size="xs" c="dimmed" fw={600}>
                      Filtrer på
                    </Text>
                    <SegmentedControl
                      size="xs"
                      radius="md"
                      color="blue"
                      value={filterMode}
                      onChange={(value) => dispatch(changeOccurrenceFilterModeThunk(value))}
                      data={[
                        { value: "listType", label: "Liste" },
                        { value: "responsibility", label: "Ansvar" },
                      ]}
                    />
                  </Stack>

                  <DrofusOccurrenceValidationFilterMenu
                    label={activeFilterLabel}
                    value={activeFilterValue}
                    emptyLabel={activeFilterEmptyLabel}
                    options={activeFilterOptions}
                    disabled={activeFilterDisabled}
                    tooltip={activeFilterTooltip}
                    onChange={(next) => dispatch(changeOccurrenceFilterValueThunk(next))}
                  />
                </MGroup>
              </Stack>
            </MGroup>

            <MGroup className="occurrence-actions" gap="xs" align="center" wrap="wrap">
              <RevalidateDrofusOccurrenceValidationButton />
              <DrofusRoomOccurrencesLiveTable />
            </MGroup>
          </Stack>
        </Box>
      </Stack>

      <Divider my="xs" />

      {specified ? (
        <ArticleValidationExclusionsTable diffItems={diffs} />
      ) : (
        <Alert color="gray" title="Ingen programmert validering">
          Ingen spesifiserte artikler-validering funnet for rommet.
        </Alert>
      )}
    </Card>
  );
};
